/**
 * Shared "generation" hook for studio panels.
 *
 * FROZEN SHARED UTIL — owned by the integrator. Per the product
 * decision, the studio does NOT call a real image/video/audio provider
 * tonight: every "Generate" button builds its prompt, shows real
 * loading → ready states, and resolves to a PLACEHOLDER asset via the
 * stub below. The single TODO seam (`PROVIDER_HOOK`) is where a real
 * provider call gets wired in later. Panels should route ALL generation
 * through this util so there's exactly one integration point.
 */

export type GenerationKind = "image" | "video" | "audio";

export interface GenerationRequest {
  kind: GenerationKind;
  /** Human/AI prompt assembled by the panel (character + location + camera + mood). */
  prompt: string;
  /** Optional aspect hint, e.g. "16:9", "1:1", "9:16". */
  aspect?: string;
  /** Optional palette (from the mood board's visual direction) to tint placeholders. */
  palette?: string[];
  /** Stable seed so the same request yields the same placeholder. */
  seed?: string;
}

export interface GenerationResult {
  kind: GenerationKind;
  /** data: URL (image) or null for video/audio placeholders. */
  url: string | null;
  prompt: string;
  /** True while no real provider is wired (always true tonight). */
  placeholder: boolean;
}

/**
 * THE PROVIDER SEAM. Replace this with a real worker route / provider
 * call to make generation live. Returns null to signal "no provider —
 * fall back to placeholder".
 */
async function PROVIDER_HOOK(
  _req: GenerationRequest,
): Promise<GenerationResult | null> {
  // TODO(real-generation): POST _req to a worker route that calls the
  // image/video/audio provider, await the asset, return it here.
  return null;
}

/** Simulated latency so loading states are visible + feel real. */
const STUB_LATENCY_MS = 900;

function hashSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A deterministic gradient placeholder image as a data: URL. */
export function placeholderImage(
  seed: string,
  palette: string[] = [],
  label = "",
  aspect = "16:9",
): string {
  const [w, h] = aspect === "9:16" ? [108, 192] : aspect === "1:1" ? [160, 160] : [192, 108];
  const n = hashSeed(seed);
  const c1 = palette[0] ?? `hsl(${n % 360} 55% 55%)`;
  const c2 = palette[1] ?? `hsl(${(n >> 3) % 360} 45% 30%)`;
  const c3 = palette[2] ?? `hsl(${(n >> 6) % 360} 40% 18%)`;
  const labelSvg = label
    ? `<text x="${w / 2}" y="${h - 8}" font-family="monospace" font-size="7" fill="rgba(255,255,255,.7)" text-anchor="middle">${label.slice(0, 28)}</text>`
    : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c1}"/><stop offset="0.55" stop-color="${c2}"/><stop offset="1" stop-color="${c3}"/></linearGradient></defs><rect width="${w}" height="${h}" fill="url(#g)"/>${labelSvg}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Run a generation. Tries the (currently stubbed) provider; on null,
 * resolves to a deterministic placeholder after a visible delay so the
 * panel's loading → ready transition reads as real.
 */
export async function runGeneration(
  req: GenerationRequest,
): Promise<GenerationResult> {
  const live = await PROVIDER_HOOK(req);
  if (live) return live;

  await new Promise((resolve) => setTimeout(resolve, STUB_LATENCY_MS));
  const seed = req.seed ?? req.prompt;
  return {
    kind: req.kind,
    url:
      req.kind === "image"
        ? placeholderImage(seed, req.palette ?? [], req.prompt.slice(0, 24), req.aspect)
        : null,
    prompt: req.prompt,
    placeholder: true,
  };
}
